import { Button, Checkbox, Col, Form, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';
import { getCountryText } from 'components/pages/Lei/formularPages/PrehledPages/DetailInfoPrehled';

import { useJinaOsobaIsins } from 'hooks/dokumenty/useJinaOsobaIsins';
import useProfily from 'hooks/useProfily';

import { EZpusobPodani, ILikvidacePrikaz, PrikazLikvidaceGet, PrikazLikvidacePut } from 'services/dokumenty/prikazy';

import trueFalseSelect from 'data/codebooks/trueFalseSelect';

import icoValidation from 'utils/validations/icoValidation';
import isinValidation from 'utils/validations/isinValidation';

import { IJinaOsobaData } from '../../NovyDokument';

interface IZruseniJineComponent {
    nextStep: () => void;
    zadostID: number;
    jinaOsobaData: IJinaOsobaData;
    type: 'likvidace' | 'fuze' | 'prevzeti' | 'list' | 'jina';
}

const ZruseniJine: React.FC<IZruseniJineComponent> = ({ nextStep, zadostID, jinaOsobaData, type }) => {
    const { language, dokumenty } = useContext(Context);
    const { setLevel } = useProfily();
    const { confirm } = Modal;
    const [form] = Form.useForm();
    const [isinListLoading, setIco, dataIsins] = useJinaOsobaIsins();
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [refetch, setRefetch] = useState<boolean>(false);
    const [allowEdit, setAllowEdit] = useState<boolean>(false);
    const [data, setData] = useState<ILikvidacePrikaz>();
    const isSigned = data?.souhlas;

    /* const [dataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
         (dokumenty?.dokumentyPrava?.prikazy || [])
            .filter(e => e.typprikazu === (dokumenty?.selectedZadost?.podtyp! as any))[0]
            .emise.filter(e => JSON.parse(e.data).evidencecp !== EEvidenceCp.CDCPDLT)
            .map(e => {
                return {
                    labelcz: e.isin,
                    value: e.isin
                };
            }) || []
    );*/

    useEffect(() => {
        setLoading(true);
        PrikazLikvidaceGet(zadostID, type)
            .then(resp => {
                setData(resp);
                !dataIsins && setIco(resp.idmb);
                form.setFieldsValue({
                    souhlas: resp.souhlas
                });
                setIsChecked(!!resp.souhlas);
            })
            .catch(() => setData({} as ILikvidacePrikaz))
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID, refetch]);

    useEffect(() => {
        allowEdit && form.submit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowEdit]);

    return (
        <LoadingWrapper loading={loading}>
            {data && jinaOsobaData && (
                <>
                    <FormWrapper
                        outForm={form}
                        onFinish={values => {
                            setLoading(true);
                            const dataToSend = {
                                ...(values as any),
                                datum: values.datum.format('YYYY-MM-DD'),
                                ...(typeof values.potvrlikv === 'boolean' && {
                                    potvrlikv: String(values.potvrlikv)
                                }),
                                ...((type === 'likvidace' || type === 'fuze' || type === 'list') && {
                                    vypis: !!values.vypis
                                }),
                                ...(allowEdit && {
                                    souhlas: false
                                })
                            };
                            delete dataToSend.idmbSpolecnost;
                            delete dataToSend.adresaSpolecnost;
                            delete dataToSend.nazevSpolecnost;
                            PrikazLikvidacePut(zadostID, dataToSend, type)
                                .then(() => {
                                    setLevel('dokumenty', {
                                        ...dokumenty,
                                        selectedZadost: {
                                            ...dokumenty?.selectedZadost!,
                                            isin: values.isin
                                        },
                                        jinaOsobaZadostData: dataToSend
                                    });
                                    if (!allowEdit) {
                                        nextStep();
                                    } else {
                                        setRefetch(pv => !pv);
                                        setAllowEdit(false);
                                    }
                                })
                                .finally(() => setLoading(false));
                        }}
                    >
                        <Title text="DOK_JINA_IDENTIFIKACE_OSOBA" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={jinaOsobaData.osobaData?.spolecnost.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmbSpolecnost"
                            notRequired={true}
                        />
                        <InputComponent
                            initialValue={jinaOsobaData.osobaData?.spolecnost.nazev}
                            label="PRIKAZ_LIKVIDACE_NAZEV"
                            name="nazevSpolecnost"
                            disabled={true}
                            notRequired={true}
                        />
                        <InputComponent
                            initialValue={`${jinaOsobaData.osobaData?.spolecnost.adma}, ${
                                jinaOsobaData.osobaData?.spolecnost.mema
                            }, ${jinaOsobaData.osobaData?.spolecnost.pscm}, ${getCountryText(
                                jinaOsobaData.osobaData?.spolecnost.kost || '',
                                language
                            )}`}
                            label="PRIKAZ_LIKVIDACE_JINA_SIDLO"
                            name="adresaSpolecnost"
                            disabled={true}
                            notRequired={true}
                        />
                        {/* <SelectComponent
                            name="isin"
                            labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                            defaultValue={data?.isin}
                            data={dataIsins}
                            isFormItem={true}
                        />*/}

                        <Title level={5} text={type !== 'jina' ? 'PRIKAZ_LIKVIDACE_TITLE' : 'PRIKAZ_EMITENT_TITLE'} />
                        <InputComponent
                            initialValue={data?.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmb"
                            maxLength={30}
                            rules={icoValidation()}
                            onBlur={ev => {
                                setIco(ev.target.value);
                            }}
                            disabled={isSigned}
                        />
                        <InputComponent
                            initialValue={data?.nazev}
                            label="ISIN_IDENTIFIKACE_FIRMA"
                            name="nazev"
                            disabled={isSigned}
                        />

                        {!dataIsins || dataIsins.length ? (
                            <LoadingWrapper loading={isinListLoading}>
                                <SelectComponent
                                    name="isin"
                                    labelParagraph={'ISIN'}
                                    defaultValue={data?.isin}
                                    data={dataIsins || []}
                                    isFormItem={true}
                                    disabled={isSigned}
                                />
                            </LoadingWrapper>
                        ) : (
                            <InputComponent
                                label="ISIN"
                                name="isin"
                                type="text"
                                maxLength={12}
                                rules={isinValidation()}
                                initialValue={data?.isin}
                                capitalize={true}
                                disabled={isSigned}
                            />
                        )}

                        <DatePickerComponent
                            name="datum"
                            initialValue={data?.datum ? dayjs(data.datum) : undefined}
                            // disabledDate={current => current < dayjs().add(3, 'days')}
                            // infoTooltipText="PRIKAZ_LIKVIDACE_DATUM_TOOLTIP"
                            label={
                                type === 'prevzeti' || type === 'jina'
                                    ? 'PRIKAZ_PREVOD_DATUM'
                                    : 'PRIKAZ_LIKVIDACE_DATUM'
                            }
                            disabled={isSigned}
                        />
                        {type === 'jina' && (
                            <InputTextArea
                                rows={4}
                                name="popis"
                                initialValue={data?.popis}
                                label="PRIKAZ_LIKVIDACE_POPIS"
                                disabled={isSigned}
                            />
                        )}
                        {(type === 'likvidace' || type === 'fuze' || type === 'list') && (
                            <>
                                <SelectComponent
                                    name="typzpusob"
                                    labelParagraph={'PRIKAZ_LIKVIDACE_ZRUSENI_SELECT'}
                                    defaultValue={data?.typzpusob}
                                    data={[
                                        {
                                            value: EZpusobPodani.MD,
                                            labelcz: 'PRIKAZ_VYTESNENI_ZPUSOB_MD'
                                        },
                                        {
                                            value: EZpusobPodani.NEPOZADUJE,
                                            labelcz: 'PRIKAZ_LIKVIDACE_ZRUSENI_NEPOZADUJE'
                                        }
                                    ]}
                                    isFormItem={true}
                                    disabled={isSigned}
                                    onChange={() => rerender(pv => !pv)}
                                />
                                {form.getFieldValue('typzpusob') === 'MD' && (
                                    <Row
                                        style={{
                                            top: -10,
                                            position: 'relative'
                                        }}
                                    >
                                        <Form.Item
                                            style={{
                                                fontWeight: 600
                                            }}
                                            name="vypis"
                                            initialValue={data?.vypis || false}
                                            valuePropName="checked"
                                            rules={[
                                                {
                                                    required: true,
                                                    transform: value => value || undefined,
                                                    type: 'boolean',
                                                    message: (
                                                        <div className="checkbox-component-rulesmg">
                                                            <TranslateText>POTVRDIT_SOUHLAS</TranslateText>
                                                        </div>
                                                    )
                                                }
                                            ]}
                                        >
                                            <Checkbox
                                                style={{
                                                    position: 'relative',
                                                    top: -2,
                                                    left: 4
                                                }}
                                                disabled={isSigned}
                                            >
                                                <Row>
                                                    <Paragraph
                                                        text="*"
                                                        style={{
                                                            color: '#ff4d4f',
                                                            fontSize: 20,
                                                            position: 'relative',
                                                            top: 4,
                                                            marginRight: 4
                                                        }}
                                                    />
                                                    <Paragraph
                                                        text="PRIKAZ_LIKVIDACE_ZRUSENI_VYPIS"
                                                        style={{
                                                            position: 'relative',
                                                            top: 4
                                                        }}
                                                    />
                                                </Row>
                                            </Checkbox>
                                        </Form.Item>
                                    </Row>
                                )}
                                {type === 'likvidace' && (
                                    <SelectComponent
                                        name="potvrlikv"
                                        labelParagraph={'PRIKAZ_ZRUSENI_POTVRZENI'}
                                        defaultValue={
                                            typeof data?.potvrlikv === 'boolean' ? String(data?.potvrlikv) : undefined
                                        }
                                        data={trueFalseSelect}
                                        isFormItem={true}
                                        disabled={isSigned}
                                    />
                                )}
                            </>
                        )}
                        <PrilohyTable zadostID={zadostID} required={true} dokJiny={true} disabled={isSigned} />
                        <Title level={5} text="PRIKAZ_FORMA_PODMINKY_TITLE" />

                        <Paragraph
                            text={'PRIKAZ_LIKVIDACE_ZRUSENI_JINAOSOBA_TEXT'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Paragraph
                            text={
                                type === 'jina'
                                    ? 'PRIKAZ_LIKVIDACE_ZRUSENI_TEXT2_JINA'
                                    : 'PRIKAZ_LIKVIDACE_ZRUSENI_TEXT2'
                            }
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Row
                            style={{
                                marginTop: 26
                            }}
                        >
                            <CheckboxComponent
                                name="souhlas"
                                checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                                onChange={() => setIsChecked(pv => !pv)}
                                checked={isChecked}
                                value={isChecked}
                                disabled={isSigned}
                            />
                        </Row>
                        <Row className="button-row">
                            <Col>
                                <ButtonComponent
                                    buttontext="POKRACOVAT"
                                    id="isinPokracovatBtn"
                                    disabled={!isChecked}
                                    htmlType="submit"
                                />
                            </Col>
                        </Row>
                    </FormWrapper>
                </>
            )}
            {isSigned && (
                <Button
                    style={{
                        top: '30%',
                        right: 10,
                        position: 'fixed',
                        zIndex: 999
                    }}
                    type="primary"
                    onClick={() =>
                        confirm({
                            title: <TranslateText>DOKUMENTY_OSOBY_ZMENIT_JINA</TranslateText>,
                            cancelText: <TranslateText>NE</TranslateText>,
                            okText: <TranslateText>ANO</TranslateText>,
                            onOk() {
                                setAllowEdit(true);
                            }
                        })
                    }
                >
                    <TranslateText>POVOLIT_EDITACI</TranslateText>
                </Button>
            )}
        </LoadingWrapper>
    );
};

export default ZruseniJine;
